import { nativeHorizontalCarousels } from 'src/components/carouselPolicy'
import { Theme } from '@mui/material/styles'

import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { PageChrome } from 'src/pageBuilder/config'

// Keep page wrappers out of horizontal scrolling; the carousels own that axis.
const pageOverflow = nativeHorizontalCarousels ? {
  '@supports (overflow: clip)': { overflowX: 'clip' as const },
  overscrollBehaviorX: 'none' as const,
} : {}

const useStyles = makeStyles<Theme, PageChrome>((theme: Theme) =>
  createStyles({
    '@global': nativeHorizontalCarousels ? {
      '#__next': pageOverflow,
    } : {},

    layout: props => ({
      display: 'grid',
      width: '100%',
      maxWidth: '100%',
      minHeight: '100vh',
      overflowX: 'hidden',
      ...pageOverflow,

      gridTemplateAreas: `
        "h"
        "c"
        "f"
      `,
      gridTemplateRows: 'min-content auto min-content',

      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
      },

      [theme.breakpoints.up('lg')]: props.builderEnabled
        ? {
            gridTemplateAreas: props.navigationPosition === 'top' && props.navigationDisplay === 'separate'
              ? `
                "h"
                "n"
                "c"
                "f"
              `
              : `
                "h"
                "c"
                "f"
              `,
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: props.navigationPosition === 'top' && props.navigationDisplay === 'separate'
              ? 'min-content min-content auto min-content'
              : 'min-content auto min-content',
          }
        : {
            gridTemplateAreas: `
              "h h h h"
              "x n c y"
              "f f f f"
            `,
            gridTemplateColumns: 'auto 240px minmax(723px, 1000px) auto',
          },

      '&.loading': {
        position: 'absolute',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
      },
    }),

    header: props => ({
      gridArea: 'h',
      ...(props.builderEnabled && props.hideLegacyHeader && props.navigationPosition === 'top' ? {
        // Keep the real header in flow from the first server render. A fixed
        // bar plus a measured placeholder changes every page after hydration.
        '@supports (overflow: clip)': {
          position: 'sticky',
          top: 0,
          alignSelf: 'start',
          zIndex: theme.zIndex.appBar,
        },
      } : {}),
    }),

    navigation: props => ({
      gridArea: 'n',
      display: props.builderEnabled
        ? 'none'
        : undefined,
      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
      paddingLeft: '12px',
      paddingRight: '12px',
    }),

    topNavigation: props => ({
      gridArea: 'n',
      display: props.builderEnabled
        && props.navigationPosition === 'top'
        && props.navigationDisplay === 'separate'
        ? 'block'
        : 'none',
    }),

    main: props => ({
      gridArea: 'c',
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'hidden',
      ...pageOverflow,
      paddingBottom: props.builderEnabled ? 0 : theme.spacing(6),
      backgroundColor: props.builderEnabled ? 'var(--page-builder-background, transparent)' : undefined,
      [theme.breakpoints.down('sm')]: {
        paddingBottom: props.builderEnabled ? 0 : theme.spacing(4),
      },
    }),

    footer: {
      gridArea: 'f',
    },

  })
)

export default useStyles
